import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart, User, LogOut, Plus, Shield, UserCircle, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { meApi } from '../api/client';
import AuthModal from './AuthModal';
import ListingForm from './ListingForm';

const navItems = [
  { label: 'Inserate', path: '/inserate' },
  { label: 'Clubs', path: '/clubs' },
  { label: 'Jobs', path: '/jobs' },
  { label: 'Immobilien', path: '/immobilien' },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showMenu, setShowMenu] = useState(false);
  const [unread, setUnread] = useState(0);
  const { user, logout } = useAuth();
  const { count: favCount } = useFavorites();
  const { pathname } = useLocation();

  useEffect(() => {
    let active = true;
    let interval;
    const fetch = () => {
      if (!user) { setUnread(0); return; }
      meApi.unreadCount().then((d) => { if (active) setUnread(d.count || 0); }).catch(() => {});
    };
    fetch();
    if (user) interval = setInterval(fetch, 30000);
    return () => { active = false; if (interval) clearInterval(interval); };
  }, [user, pathname]);

  const onCreateClick = () => {
    if (!user) { setAuthMode('login'); setAuthOpen(true); return; }
    setCreateOpen(true);
  };

  const MessagesBadge = ({ className = '' }) => (
    <Link to="/mein-profil?tab=messages" aria-label="Nachrichten"
      className={`relative p-2 text-[#b8a9a3] hover:text-white rounded-md hover:bg-[#241a1f] ${className}`}>
      <MessageCircle className="w-5 h-5" />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#a83a5b] text-[10px] font-semibold text-white flex items-center justify-center">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );

  const FavButton = ({ className = '' }) => (
    <Link to="/favoriten" aria-label="Favoriten"
      className={`relative p-2 text-[#b8a9a3] hover:text-[#ff8aa3] rounded-md hover:bg-[#241a1f] ${className}`}>
      <Heart className={`w-5 h-5 ${favCount > 0 ? 'fill-[#ff4d6d] text-[#ff4d6d]' : ''}`} />
      {favCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#241a1f] border border-[#a83a5b] text-[10px] font-semibold text-[#ff8aa3] flex items-center justify-center">
          {favCount > 99 ? '99+' : favCount}
        </span>
      )}
    </Link>
  );

  return (
    <>
      <header className="sticky top-0 z-40 bg-[#1a1417]/95 backdrop-blur-md border-b border-[#2a1f24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/inserate" className="flex items-center gap-2 group">
              <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#a83a5b] to-[#5e1c30] flex items-center justify-center">
                <Heart className="w-4 h-4 text-white fill-white" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-serif text-white text-lg tracking-tight">Matchdate</span>
                <span className="text-[10px] text-[#c19a8a] tracking-[0.2em] uppercase -mt-1">.ch</span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const active = pathname === item.path || (item.path === '/inserate' && pathname === '/');
                return (
                  <Link key={item.path} to={item.path}
                    className={`px-4 py-2 text-sm rounded-md transition-colors ${
                      active ? 'text-white bg-[#2a1f24]' : 'text-[#b8a9a3] hover:text-white hover:bg-[#241a1f]'
                    }`}>
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* DESKTOP right side */}
            <div className="hidden md:flex items-center gap-2">
              {user?.is_admin && (
                <Link to="/admin" className="px-3 py-2 text-sm text-[#c19a8a] hover:text-white inline-flex items-center gap-1.5">
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              <FavButton />
              {user && <MessagesBadge />}
              {user ? (
                <div className="relative">
                  <button onClick={() => setShowMenu(!showMenu)}
                    className="px-3 py-2 text-sm rounded-md text-[#b8a9a3] hover:text-white hover:bg-[#241a1f] inline-flex items-center gap-2">
                    <UserCircle className="w-4 h-4 text-[#c19a8a]" /> {user.name}
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-[#1a1417] border border-[#2a1f24] rounded-md shadow-lg py-1 z-50">
                      <Link to="/mein-profil" onClick={() => setShowMenu(false)}
                        className="block px-4 py-2 text-sm text-[#b8a9a3] hover:text-white hover:bg-[#241a1f] inline-flex items-center gap-2 w-full">
                        <UserCircle className="w-4 h-4" /> Mein Profil
                      </Link>
                      <Link to="/favoriten" onClick={() => setShowMenu(false)}
                        className="block px-4 py-2 text-sm text-[#b8a9a3] hover:text-white hover:bg-[#241a1f] inline-flex items-center gap-2 w-full">
                        <Heart className="w-4 h-4" /> Favoriten ({favCount})
                      </Link>
                      <button onClick={() => { logout(); setShowMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-[#b8a9a3] hover:text-white hover:bg-[#241a1f] inline-flex items-center gap-2">
                        <LogOut className="w-4 h-4" /> Abmelden
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => { setAuthMode('login'); setAuthOpen(true); }}
                  className="px-4 py-2 text-sm text-[#b8a9a3] hover:text-white inline-flex items-center gap-2">
                  <User className="w-4 h-4" /> Anmelden
                </button>
              )}
              <button onClick={onCreateClick}
                className="px-4 py-2 text-sm rounded-md bg-[#a83a5b] hover:bg-[#bf456b] text-white font-medium inline-flex items-center gap-1.5">
                <Plus className="w-4 h-4" /> Inserat aufgeben
              </button>
            </div>

            {/* MOBILE right side: icons always visible */}
            <div className="md:hidden flex items-center gap-1">
              <FavButton />
              {user ? (
                <>
                  <MessagesBadge />
                  <Link to="/mein-profil" aria-label="Mein Profil"
                    className="p-2 text-[#b8a9a3] hover:text-white rounded-md hover:bg-[#241a1f]">
                    <UserCircle className="w-5 h-5" />
                  </Link>
                </>
              ) : (
                <button onClick={() => { setAuthMode('login'); setAuthOpen(true); }}
                  aria-label="Anmelden"
                  className="p-2 text-[#b8a9a3] hover:text-white rounded-md hover:bg-[#241a1f]">
                  <User className="w-5 h-5" />
                </button>
              )}
              <button className="p-2 text-white" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="md:hidden border-t border-[#2a1f24] py-3 space-y-1">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 text-sm text-[#b8a9a3] hover:text-white hover:bg-[#241a1f] rounded-md">
                  {item.label}
                </Link>
              ))}
              {user?.is_admin && (
                <Link to="/admin" onClick={() => setMobileOpen(false)}
                  className="block px-4 py-2.5 text-sm text-[#c19a8a] inline-flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
              {user && (
                <button onClick={() => { logout(); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-2.5 text-sm text-[#b8a9a3] hover:text-white">
                  <LogOut className="w-4 h-4 inline mr-2" /> Abmelden
                </button>
              )}
              <div className="pt-2">
                <button onClick={() => { onCreateClick(); setMobileOpen(false); }}
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-[#a83a5b] text-white font-medium">
                  <Plus className="w-4 h-4 inline mr-1" /> Inserat aufgeben
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={authMode} />
      <ListingForm open={createOpen} onClose={() => setCreateOpen(false)}
        onSuccess={(l) => { window.location.href = `/inserate/${l.slug}`; }} />
    </>
  );
};

export default Header;
